import { useEffect, useState, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { api, SceneForgeApiError } from '../lib/api';
import { useActiveTeam } from '../lib/teamContext';
import type { SceneForgeUserMe } from '../lib/types';
import { Spinner } from './Spinner';

// Route guard for every authenticated SceneForge surface.
//
// Defense layer 5 from SCENEFORGE_PLAN.md §7.7 + V5 team-select flow
// from SCENEFORGE_V5_PLATFORM.md §8.4. On mount, hits
// GET /v1/sceneforge/me, then branches on session + membership:
//
//   - no Supabase session              → redirect to /login
//   - 401 from /me                     → redirect to /login (token expired)
//   - 403 sceneforge_access_denied     → redirect to /not-provisioned
//   - 200 { active: false }            → redirect to /not-provisioned
//   - admin with 0 memberships, on a
//     non-/admin path                  → redirect to /admin (must create a team)
//   - 0 memberships and not admin      → redirect to /not-provisioned
//   - exactly 1 membership, no active
//     team selected                    → auto-select it (silent)
//   - 2+ memberships, no active team   → redirect to /team-select
//   - persisted active team_id no
//     longer in memberships            → clear it and re-resolve above
//   - team-route OK                    → render children
//
// Renders nothing visible while checking other than a small grayscale spinner
// — never a flash of dashboard content for unauthorized or wrong-team users.

interface Props {
  children: (user: SceneForgeUserMe) => ReactNode;
  /** When true, allows render even with no active team selected. Used by
   *  `/team-select` and `/admin` so they don't redirect to themselves. */
  allowNoTeam?: boolean;
  /** When true, only renders for `user.is_admin === true`. Used by `/admin`. */
  adminOnly?: boolean;
}

type Phase =
  | { kind: 'checking' }
  | { kind: 'no_session' }
  | { kind: 'not_provisioned' }
  | { kind: 'needs_team_select' }
  | { kind: 'needs_admin_bootstrap' }
  | { kind: 'forbidden_non_admin' }
  | { kind: 'ok'; user: SceneForgeUserMe };

export function RequireSceneForgeAccess({
  children,
  allowNoTeam = false,
  adminOnly = false,
}: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: 'checking' });
  const location = useLocation();
  const { activeTeam, setActiveTeam } = useActiveTeam();

  useEffect(() => {
    const ac = new AbortController();
    let cancelled = false;

    async function run() {
      const { data: sessionData } = await supabase.auth.getSession();
      if (cancelled) return;

      if (!sessionData.session) {
        setPhase({ kind: 'no_session' });
        return;
      }

      try {
        const user = await api.me(ac.signal);
        if (cancelled) return;
        if (!user.active) {
          setPhase({ kind: 'not_provisioned' });
          return;
        }

        if (adminOnly && !user.is_admin) {
          setPhase({ kind: 'forbidden_non_admin' });
          return;
        }

        const memberships = user.team_memberships ?? [];

        // Reconcile any persisted active team against the membership list.
        // If the active team no longer applies (e.g. user was removed in
        // another tab), clear it so we fall through to the picker logic.
        if (activeTeam) {
          const stillMember = memberships.some(
            (m) => m.team_id === activeTeam.team_id,
          );
          if (!stillMember && !user.is_admin) {
            setActiveTeam(null);
          }
        }

        // 0-membership branches.
        if (memberships.length === 0) {
          if (user.is_admin) {
            // Admin with no teams MUST land on /admin to create one.
            if (location.pathname.startsWith('/admin') || allowNoTeam) {
              setPhase({ kind: 'ok', user });
              return;
            }
            setPhase({ kind: 'needs_admin_bootstrap' });
            return;
          }
          setPhase({ kind: 'not_provisioned' });
          return;
        }

        // Single-membership: auto-select silently.
        if (memberships.length === 1 && !activeTeam) {
          const only = memberships[0];
          if (only) {
            setActiveTeam({ team_id: only.team_id, team_name: only.team_name });
            setPhase({ kind: 'ok', user });
            return;
          }
        }

        // Multi-membership with no team yet → route to picker.
        if (memberships.length > 1 && !activeTeam) {
          if (allowNoTeam) {
            setPhase({ kind: 'ok', user });
            return;
          }
          setPhase({ kind: 'needs_team_select' });
          return;
        }

        setPhase({ kind: 'ok', user });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof SceneForgeApiError) {
          if (err.status === 401) {
            setPhase({ kind: 'no_session' });
            return;
          }
          if (err.status === 403) {
            setPhase({ kind: 'not_provisioned' });
            return;
          }
        }
        // For any other error (network, 500, etc.) we fall back to the
        // login screen rather than blocking the user on a blank page.
        setPhase({ kind: 'no_session' });
      }
    }

    void run();
    return () => {
      cancelled = true;
      ac.abort();
    };
    // Intentionally exclude activeTeam / setActiveTeam — we only want to
    // re-run the membership reconciliation on actual navigations, not on
    // every team switch (which would loop with the auto-select branch).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, adminOnly, allowNoTeam]);

  if (phase.kind === 'checking') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-zinc-950">
        <Spinner size={28} label="Verifying access" />
      </div>
    );
  }
  if (phase.kind === 'no_session') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (phase.kind === 'not_provisioned') {
    return <Navigate to="/not-provisioned" replace />;
  }
  if (phase.kind === 'forbidden_non_admin') {
    return <Navigate to="/dashboard" replace />;
  }
  if (phase.kind === 'needs_team_select') {
    return <Navigate to="/team-select" replace />;
  }
  if (phase.kind === 'needs_admin_bootstrap') {
    return <Navigate to="/admin" replace />;
  }
  return <>{children(phase.user)}</>;
}
